import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { GridDateColumn } from 'components/common-form/builder/form/node/field/SimpleGrid/GridDateColumn';
import { IndependentAmountField } from 'components/PostTrade/TradeAllocation/components/IndependentAmountField';
import { useTradeAllocationValueDatePresets } from 'components/PostTrade/TradeAllocation/hooks/useTradeAllocationValueDatePresets';
import { MonoDatePickerWithCustomPreset } from 'components/shared/DatePicker/MonoDatePicker';
import { asIsoDate } from 'components/shared/DatePicker/utils';
import { ValidationFeedbackMessage } from 'components/shared/FormGroup/ErrorMessages/ValidationFeedbackMessage';
import { NewAmountInput } from 'components/shared/NewAmountInput';
import { PercentWithUnit } from 'components/shared/Percent/PercentWithUnit';
import { Switch } from 'components/shared/Switch/Switch';
import { TextField } from 'components/shared/TextField/TextField';
import { addDays, isValid, parseISO } from 'date-fns';
import { useSPIntl } from 'hooks/useIntl';
import type { Custodian } from 'models/custodian';
import { TranslatedValidationError } from 'models/formError';
import { IndependentAmount } from 'models/forms/TradeAllocationFormResult';
import { FC } from 'react';
import type { TradeAllocationRowModel } from 'state/tradeAllocationForm';
import { translateValidationError } from 'utils/errors/form-validation-errors';

import '../style.scss';

export interface TradeAllocationtRowProps {
  allocation: TradeAllocationRowModel;
  index: number;
  custodians: ReadonlyArray<Custodian>;
  isDisabled: boolean;
  hasIndependentAmount: boolean;
  hasAnyIndependentAmount: boolean;
  onAllocationChanged: (allocation: TradeAllocationRowModel) => void;
  onAllocationRemoved: () => void;
}

const createDefaultIndependentAmount = (tradeDate: Nullable<string>): IndependentAmount => ({
  dealId: null,
  type: 'Percentage',
  amount: null,
  percentageAmount: null,
  valueDate: createDefaultIndependentAmountValueDate(tradeDate),
  payer: 'Cpty',
});

const createDefaultIndependentAmountValueDate = (tradeDate: Nullable<string>): string | null => {
  if (!tradeDate) {
    return null;
  }

  const parsedTradeDate = parseISO(tradeDate);
  if (!isValid(parsedTradeDate)) {
    return null;
  }

  return asIsoDate(addDays(parsedTradeDate, 1));
};

export const TradeAllocationRow: FC<TradeAllocationtRowProps> = ({
  allocation,
  index,
  custodians,
  isDisabled,
  onAllocationChanged,
  onAllocationRemoved,
  hasIndependentAmount,
  hasAnyIndependentAmount,
}) => {
  const intl = useSPIntl();

  const { independentAmount } = allocation;
  const { valueDatePresets, valueDatePresetTranslations } = useTradeAllocationValueDatePresets(allocation.tradeDate);
  const custodianCode = allocation.custodianCode || '';
  const foundCustodian = custodians.find(c => c.id === custodianCode);
  const custodianLabel = foundCustodian?.label || custodianCode;
  const custodianValidation: TranslatedValidationError | undefined =
    !isDefined(foundCustodian) && custodianCode !== ''
      ? translateValidationError(
          {
            translationKey: 'quote.errorMessages.incoherentSelectedValueInList',
            values: { field: 'custodian', values: custodianCode },
          },
          intl,
          '',
        )
      : undefined;

  const onIndependentAmountToggled = (isChecked: boolean) => {
    onAllocationChanged({
      ...allocation,
      isExpanded: isChecked,
      independentAmount: isChecked
        ? (independentAmount ?? createDefaultIndependentAmount(allocation.tradeDate))
        : undefined,
    });
  };

  const onIndependentAmountChanged = (updatedIndependentAmount: IndependentAmount) => {
    onAllocationChanged({
      ...allocation,
      isExpanded: true,
      independentAmount: updatedIndependentAmount,
    });
  };

  return (
    <>
      <tr data-e2e="allocation-row">
        <td className="text-center" style={{ minWidth: '150px', width: '15%' }} title={allocation.dealId}>
          {allocation.dealId !== '' && (
            <TextField id="tradeAllocation-dealId" value={allocation.dealId + ''} isReadOnly isDisabled={isDisabled} />
          )}
          {allocation.isManuallyAdded === true && (
            <button
              className="btn btn-icon btn-flat-primary"
              type="button"
              onClick={onAllocationRemoved}
              disabled={isDisabled}
              data-e2e="remove-row"
            >
              <i className="icon clickable text-secondary">cancel</i>
            </button>
          )}
        </td>
        <td className="w-25">
          <GridDateColumn
            isDisabled={isDisabled}
            value={allocation.tradeDate}
            id={`tradeAllocation-tradeDate-${index}`}
            e2e={`tradeAllocation-tradeDate-input-${index}`}
            isClearable
            hasErrors={allocation.tradeDate === null || allocation.tradeDate === ''}
            setCellValue={(_, value) =>
              onAllocationChanged({
                ...allocation,
                tradeDate: value,
              })
            }
          />
        </td>
        <td className="text-nowrap w-25" style={{ minWidth: '220px' }}>
          <div className="d-flex">
            <i className="icon text-info">how_to_reg</i>
            <select
              className={classNames('form-select form-control-md', {
                'border-bottom border-warning': custodianCode === '',
              })}
              id={`tradeAllocation-Custodian-${index}`}
              data-e2e="tradeAllocation-custodian-input"
              value={custodianCode}
              disabled={isDisabled}
              title={custodianLabel}
              onChange={event =>
                onAllocationChanged({
                  ...allocation,
                  custodianCode: event.target.value,
                })
              }
            >
              {!foundCustodian && (
                <option value={custodianCode} key={custodianCode}>
                  {custodianCode}
                </option>
              )}
              {custodians.map(({ id, label, eliotCode }) => (
                <option value={id} key={id}>
                  {eliotCode ? `${label} (${eliotCode})` : label}
                </option>
              ))}
            </select>
          </div>
          <ValidationFeedbackMessage validationError={custodianValidation} />
        </td>
        <td className="text-nowrap w-25" style={{ minWidth: '140px' }}>
          <NewAmountInput
            value={allocation.notional ?? null}
            onValueChanged={value =>
              onAllocationChanged({
                ...allocation,
                notional: value,
              })
            }
            precision={4}
            currency={allocation.currency}
            hasError={allocation.notional === null}
            e2e="tradeAllocation-notional-input"
            decimalPadding={false}
            isDisabled={isDisabled}
          />
        </td>
        <td className="trade-allocation-col-offer-price" style={{ minWidth: '102px' }}>
          <PercentWithUnit
            isDisabled={isDisabled}
            e2e="tradeAllocation-offerPrice"
            value={allocation.offerPrice}
            unit={allocation.offerPriceUnit}
            hasError={allocation.offerPrice === undefined}
            onChange={value =>
              onAllocationChanged({
                ...allocation,
                offerPrice: value,
              })
            }
            precision={4}
            className={allocation.hasOfferPrice ? 'price-text' : ''}
          />
        </td>
        <td className="text-nowrap" style={{ minWidth: '110px' }}>
          <PercentWithUnit
            isDisabled={isDisabled}
            e2e="tradeAllocation-distributorUf"
            value={allocation.distributorUpFront}
            hasError={allocation.distributorUpFront === undefined}
            onChange={value =>
              onAllocationChanged({
                ...allocation,
                distributorUpFront: value,
              })
            }
            precision={4}
          />
        </td>
        <td className="text-nowrap" style={{ minWidth: '110px' }}>
          <PercentWithUnit
            isDisabled={isDisabled}
            e2e="tradeAllocation-custodianUf"
            value={allocation.custodianUpFront}
            hasError={allocation.custodianUpFront === undefined}
            onChange={value =>
              onAllocationChanged({
                ...allocation,
                custodianUpFront: value,
              })
            }
            precision={4}
          />
        </td>
        <td className="trade-allocation-col-sales-margin">
          <PercentWithUnit
            isDisabled={isDisabled}
            e2e="tradeAllocation-salesMargin"
            value={toPercent(allocation.salesMargin)}
            isInfo={allocation.salesMargin === undefined}
            onChange={value =>
              onAllocationChanged({
                ...allocation,
                salesMargin: toValue(value),
              })
            }
            precision={4}
          />
        </td>
        <td className="w-25">
          <GridDateColumn
            isDisabled={isDisabled}
            value={sanitizeDate(allocation.valueDate) ?? null}
            id={`tradeAllocation-valueDate-${index}`}
            e2e={`tradeAllocation-valueDate-input-${index}`}
            isClearable
            align="end"
            setCellValue={(_, value) =>
              onAllocationChanged({
                ...allocation,
                valueDate: sanitizeDate(value) ?? undefined,
              })
            }
          />
        </td>
        {hasIndependentAmount && (
          <td
            className={classNames('align-middle trade-allocation-ia-main-cell w-25', {
              'is-expanded': hasAnyIndependentAmount,
            })}
          >
            <div className="d-flex align-items-center ia-fade-content">
              <div className="ia-always-visible">
                <Switch
                  id={`tradeAllocation-ia-toggle-${index}`}
                  value={independentAmount !== undefined}
                  isDisabled={isDisabled}
                  e2e="tradeAllocation-ia-toggle"
                  onToggleSwitch={onIndependentAmountToggled}
                />
              </div>
              {independentAmount && (
                <IndependentAmountField
                  independentAmount={independentAmount}
                  index={index}
                  currency={allocation.currency}
                  isDisabled={isDisabled}
                  onIndependentAmountChanged={onIndependentAmountChanged}
                />
              )}
            </div>
          </td>
        )}
        {hasIndependentAmount && (
          <td
            className={classNames('align-middle trade-allocation-ia-value-date-cell ia-fade-content', {
              'is-expanded': hasAnyIndependentAmount,
            })}
          >
            {hasAnyIndependentAmount && independentAmount && (
              <MonoDatePickerWithCustomPreset
                id={`independentAmountValueDate-${index}`}
                className="trade-allocation-ia-value-date"
                disabled={isDisabled}
                value={sanitizeDate(independentAmount.valueDate)}
                isClearable={false}
                datePresets={valueDatePresets}
                datePresetTranslations={valueDatePresetTranslations}
                align="end"
                onChange={value =>
                  onIndependentAmountChanged({
                    ...independentAmount,
                    valueDate: sanitizeDate(value) ?? null,
                  })
                }
              />
            )}
            {hasAnyIndependentAmount && !independentAmount && '-'}
          </td>
        )}
      </tr>
    </>
  );

  function toPercent(value: number | undefined): number | undefined {
    return value === undefined ? undefined : value * 100;
  }
  function toValue(value: number | undefined): number | undefined {
    return value === undefined ? undefined : value / 100;
  }

  function sanitizeDate(date: string | undefined | null): string | undefined | null {
    return date === '0001-01-01' ? null : date;
  }
};
